import { ofType } from 'redux-observable';
import { mapTo, switchMap } from 'rxjs/operators';
import { of, merge } from 'rxjs';

import {
   COMPLETE_SESSION,
   exerciseChallengeReady,
   exerciseReady,
   PREPARE_NEXT_CHALLENGE,
   PREPARE_NEXT_SESSION,
   submitAnswer,
   PRE_SUBMIT_ANSWER,
   setMetadata,
   setPrompt,
} from '../actions';
import {
   clearSelectedNotes,
   setChordInfo,
   setHiddenControls,
   setReferenceNotes,
   setSelectedNotes,
   setView,
} from '../../interfaces/keyboard/actions';
import {
   randomNumberInRange,
   arrayValuesToObjectKeys,
   randomNumbersInRange,
} from './util';
import { combineExerciseEpics } from '../combineExerciseEpics';

import { getRandomArrayElement } from '../../util/getRandomArrayElement';
import { getCurrentLevel } from '../getCurrentLevel';
import { keyboardModes } from '../../util/constants';
import { naturalNotes, simpleFlats, simpleSharps } from './defs';

const applyModifier = (rawIntervals, modifier, maxAllowedInterval) => {
   switch (modifier) {
      case 'doubling': {
         let newRawIntervals;
         do {
            newRawIntervals = [
               ...rawIntervals,
               getRandomArrayElement(rawIntervals) +
                  getRandomArrayElement([12, -12]),
            ];

            const minInterval = Math.min(...newRawIntervals);
            if (minInterval < 0) {
               newRawIntervals = newRawIntervals.map(
                  (noteValue) => noteValue - minInterval
               );
            }
         } while (Math.max(...newRawIntervals) > maxAllowedInterval);
         return newRawIntervals;
      }
      default:
         return rawIntervals;
   }
};

const applyAllModifiers = (rawIntervals, modifiers, maxAllowedInterval) => {
   let newRawIntervals = [...rawIntervals];
   if (modifiers == null) {
      return rawIntervals;
   }
   modifiers.forEach((modifier) => {
      newRawIntervals = applyModifier(
         newRawIntervals,
         modifier,
         maxAllowedInterval
      );
   });
   return newRawIntervals;
};
const testCondition = (notes, { contains, excludes }) => {
   if (contains) {
      for (let i = 0; i < contains.length; i += 1) {
         if (notes[contains[i]] == null) {
            return false;
         }
      }
   }

   if (excludes) {
      for (let i = 0; i < excludes.length; i += 1) {
         if (notes[excludes[i]] != null) {
            return false;
         }
      }
   }

   return true;
};

const processOptionalAndLayers = (rawIntervals, level, exerciseDocument) => {
   const levelDefinition = exerciseDocument.levels[level - 1];

   const layers = {};
   const notes = {};

   rawIntervals.forEach((note) => {
      // Plain number, No special conditions
      if (note.value == null) {
         notes[note] = note;
      }
      // Collect layers
      else if (note.layer != null) {
         if (layers[note.layer] == null) {
            layers[note.layer] = [note.value];
         } else {
            layers[note.layer] = [...layers[note.layer], note.value];
         }
      }
      // Randomly add optional notes
      else if (note.optional && Math.random() < (note.odds || 0.5)) {
         notes[note.value] = note.value;
      }
      // Add any other notes with conditions
      else if (!note.optional) {
         notes[note.value] = note.value;
      }
   });

   // add layers based on layer logic
   Object.entries(layers).forEach(([layer, rawIntervalValues]) => {
      const layerDef = levelDefinition.layers[layer];
      let values = rawIntervalValues;

      // get random values from layer
      {
         const min = layerDef.min || values.length;
         const max = layerDef.max || values.length;

         const indexes = randomNumbersInRange(randomNumberInRange(min, max), [
            0,
            values.length - 1,
         ]);

         values = indexes.map((i) => values[i]);
      }

      if (!layerDef.optional || Math.random() < (layerDef.odds || 0.5)) {
         values.forEach((noteValue) => {
            notes[noteValue] = noteValue;
         });
      }
   });

   // Remove values that don't meet conditions
   rawIntervals.forEach((note) => {
      if (note.conditions == null) {
         return;
      }

      let matches = true;

      if (note.conditions.matchAll) {
         matches =
            matches &&
            note.conditions.matchAll.reduce(
               (hasMatchedAll, currentCondition) => {
                  if (!hasMatchedAll) {
                     return false;
                  }
                  return testCondition(notes, currentCondition);
               },
               true
            );
      }

      if (note.conditions.matchAny) {
         matches =
            matches &&
            note.conditions.matchAny.reduce(
               (hasMatchedAny, currentCondition) => {
                  if (hasMatchedAny) {
                     return true;
                  }

                  return testCondition(notes, currentCondition);
               },
               false
            );
      }

      if (note.conditions.matchNone) {
         matches =
            matches &&
            !note.conditions.matchNone.reduce(
               (hasMatchedAny, currentCondition) => {
                  if (hasMatchedAny) {
                     return true;
                  }

                  return testCondition(notes, currentCondition);
               },
               false
            );
      }

      if (!matches) {
         notes[note.value] = null;
      }
   });

   return Object.values(notes).filter((note) => note != null);
};

const getRawIntervals = (level, exerciseDocument, maxAllowedInterval) => {
   const levelDefinition = exerciseDocument.levels[level - 1];
   const chordDefinitions = levelDefinition.chords;

   if (chordDefinitions) {
      return processOptionalAndLayers(
         getRandomArrayElement(chordDefinitions),
         level,
         exerciseDocument
      );
   }

   const nestedLevel = getRandomArrayElement(levelDefinition.chordsFromLevels);
   return applyAllModifiers(
      getRawIntervals(nestedLevel, exerciseDocument, maxAllowedInterval),
      levelDefinition.withModifiers,
      maxAllowedInterval
   );
};

const createChord = (
   level,
   exerciseDocument,
   [keyboardNoteRangeLow, keyboardNoteRangeHigh]
) => {
   const rawIntervals = getRawIntervals(
      level,
      exerciseDocument,
      keyboardNoteRangeHigh - keyboardNoteRangeLow
   );

   const maxInterval = Math.max(...rawIntervals);
   const root = randomNumberInRange(
      keyboardNoteRangeLow,
      keyboardNoteRangeHigh - maxInterval
   );

   return rawIntervals.map((note) => note + root);
};

const getRandomLevel = (state) => {
   const { sessionHistory } = state.activeExercise.progress;

   const currentLevel = getCurrentLevel(state);

   // Should only do challenges from the level selected, when selected
   if (state.activeExercise.levelSelect) {
      return currentLevel;
   }

   const sessionsCompleted = sessionHistory.reduce(
      (count, { level }) => (level === currentLevel ? count + 1 : count),
      0
   );
   const { previousLevelOddsPercent, previousLevelTransitionSessionCount } =
      state.activeExercise.exerciseDocument.levels[currentLevel - 1];

   const calcuatedOdds =
      previousLevelTransitionSessionCount > 0
         ? (previousLevelOddsPercent / previousLevelTransitionSessionCount) *
           sessionsCompleted
         : previousLevelOddsPercent;

   const randomNumber = randomNumberInRange(1, 100);

   return randomNumber <= calcuatedOdds
      ? randomNumberInRange(1, currentLevel - 1)
      : currentLevel;
};

const selectRandomNote = (type = 'simple') => {
   const includeNatural = type === 'simple' || type === 'natural';
   const includeSimpleSharp = type === 'simple' || type === 'simpleSharpOrFlat';
   const includeSimpleFlat = type === 'simple' || type === 'simpleSharpOrFlat';

   return getRandomArrayElement([
      ...(includeNatural ? naturalNotes : []),
      ...(includeSimpleSharp ? simpleSharps : []),
      ...(includeSimpleFlat ? simpleFlats : []),
   ]);
};

const createPrompt = (prompt, chordData) =>
   Object.entries(chordData).reduce(
      (newText, [key, val]) => newText.replace(`{:${key}}`, val),
      prompt
   );

const createQuiz = (level, exerciseDocument, [rangeLow, rangeHigh]) => {
   let invalidSelection = true;
   const levelData = exerciseDocument.levels[level - 1];
   let chordData;

   while (invalidSelection) {
      const note = selectRandomNote(levelData.notesType);

      const chord = getRandomArrayElement(levelData.chords);

      chordData = {
         noteName: note.name,
         noteOffset: note.note,

         ...chord,
      };

      const chordWithOffset = chordData.notes.map(
         (n) => n + chordData.noteOffset
      );

      invalidSelection =
         Math.max(...chordWithOffset) > rangeHigh ||
         (Math.min(...chordWithOffset) < rangeLow &&
            Math.max(...chordWithOffset) + 12 > rangeHigh);
   }
   return {
      prompt: createPrompt(levelData.prompt, chordData),
      chord: chordData,
   };
};

const getMatchingNoteCount = (reference, user) =>
   reference.reduce((count, currentNote) => {
      if (user.find((note) => note === currentNote) != null) {
         return count + 1;
      }
      return count;
   }, 0);

const getTotalRangeDifference = (reference, user) => {
   const userMax = Math.max(...user);
   const userMin = Math.min(...user);
   const referenceMax = Math.max(...reference);
   const referenceMin = Math.min(...reference);

   return Math.abs(userMax - referenceMax) + Math.abs(userMin - referenceMin);
};

const getProximityAdjustedOffset = (referenceNotes, userNotes, noteRange) => {
   let maxAdjustment = 0;
   let currentAdjustment = maxAdjustment;

   if (referenceNotes.find((note) => note < noteRange[0]) != null) {
      currentAdjustment = maxAdjustment + 12;
   }

   if (userNotes.length === 0) {
      return currentAdjustment;
   }

   {
      let outOfRange = false;
      while (!outOfRange) {
         const nextMaxAdjustment = maxAdjustment + 12;

         outOfRange =
            referenceNotes.find(
               (note) => note + nextMaxAdjustment > noteRange[1]
            ) != null;

         if (!outOfRange) {
            maxAdjustment = nextMaxAdjustment;
         }
      }
   }

   if (maxAdjustment === 0) {
      return currentAdjustment;
   }

   let needToCheckRange = true;

   // check for most matched notes
   {
      let nextAdjustment = currentAdjustment;
      let bestAdjustment = currentAdjustment;
      let bestMatchingNoteCount = getMatchingNoteCount(
         referenceNotes,
         userNotes
      );
      while (nextAdjustment < maxAdjustment) {
         nextAdjustment += 12;
         const nextMatchingCount = getMatchingNoteCount(
            // eslint-disable-next-line no-loop-func
            referenceNotes.map((note) => note + nextAdjustment),
            userNotes
         );

         if (nextMatchingCount !== bestMatchingNoteCount) {
            needToCheckRange = false;
         }

         if (nextMatchingCount > bestMatchingNoteCount) {
            bestMatchingNoteCount = nextMatchingCount;
            bestAdjustment = nextAdjustment;
         }
      }

      currentAdjustment = bestAdjustment;
   }

   // Check for proximity of notes if needed
   if (needToCheckRange) {
      let nextAdjustment = currentAdjustment;
      let bestAdjustment = currentAdjustment;
      let bestRangeDifference = getTotalRangeDifference(
         referenceNotes,
         userNotes
      );
      while (nextAdjustment < maxAdjustment) {
         nextAdjustment += 12;
         const nextRangeDifference = getTotalRangeDifference(
            // eslint-disable-next-line no-loop-func
            referenceNotes.map((note) => note + nextAdjustment),
            userNotes
         );

         if (nextRangeDifference < bestRangeDifference) {
            bestRangeDifference = nextRangeDifference;
            bestAdjustment = nextAdjustment;
         }
      }

      currentAdjustment = bestAdjustment;
   }

   return currentAdjustment;
};

const getReferenceNotesFromMetaData = (state) => {
   const chordData = state.activeExercise.metadata.chord;
   const userNotes = Object.entries(state.activeInterface.selectedNotes)
      .filter(([, selected]) => selected)
      .map(([note]) => Number(note));

   const notesWithDefaultOffset = chordData.notes.map(
      (note) => note + chordData.noteOffset
   );
   const proximityAdjustedOffset = getProximityAdjustedOffset(
      notesWithDefaultOffset,
      userNotes,
      state.activeInterface.noteRange
   );

   const notesWithProximityAdjustedOffset = notesWithDefaultOffset.map(
      (note) => note + proximityAdjustedOffset
   );
   return notesWithProximityAdjustedOffset.reduce(
      (referenceNotes, note) => ({ ...referenceNotes, [note]: true }),
      {}
   );
};

// could check if soundkit is loaded here
const prepareNextSessionEpic = (action$) =>
   action$.pipe(ofType(PREPARE_NEXT_SESSION), mapTo(exerciseReady()));

const prepareNextChallengeEpic = (action$, state$) =>
   action$.pipe(
      ofType(PREPARE_NEXT_CHALLENGE),
      switchMap(() => {
         const level = getRandomLevel(state$.value);

         if (
            state$.value.activeExercise.exerciseDocument.levels[level - 1]
               .mode === keyboardModes.fromName
         ) {
            const quiz = createQuiz(
               level,
               state$.value.activeExercise.exerciseDocument,
               state$.value.activeInterface.noteRange
            );

            return merge(
               of(clearSelectedNotes()),
               of(
                  setMetadata({
                     chord: quiz.chord,
                  })
               ),
               of(setPrompt(quiz.prompt)),
               of(setHiddenControls({ playReference: true })),
               of(exerciseChallengeReady())
            );
         }

         if (
            state$.value.activeExercise.exerciseDocument.levels[level - 1]
               .mode === keyboardModes.nameByEar
         ) {
            const levelData =
               state$.value.activeExercise.exerciseDocument.levels[level - 1];
            const chords = levelData.chords;
            const chord = getRandomArrayElement(chords);

            const maxInterval = Math.max(...chord.notes);
            const root =
               levelData.rootNote ||
               randomNumberInRange(
                  state$.value.activeInterface.noteRange[0],
                  state$.value.activeInterface.noteRange[1] - maxInterval
               );

            const allChordInfo = Object.entries(
               state$.value.activeExercise.exerciseDocument.levels[level - 1]
                  .nameParameters
            ).reduce((chordInfo, [key, parameter]) => {
               if (!parameter) {
                  return chordInfo;
               }
               const allOptions = new Set();
               chords.forEach((c) => {
                  allOptions.add(c[key]);
               });

               return {
                  ...chordInfo,
                  [key]: {
                     label: parameter.label,
                     options: [...allOptions],
                     value: chord[key],
                     selected: parameter.defaultValue,
                  },
               };
            }, {});

            return merge(
               of(clearSelectedNotes()),
               of(setView('nameSelect')),
               of(
                  setMetadata({
                     root,
                     level,
                     mode: state$.value.activeExercise.exerciseDocument.levels[
                        level - 1
                     ].mode,
                  })
               ),
               of(setChordInfo(allChordInfo)),
               of(
                  setReferenceNotes(
                     arrayValuesToObjectKeys(
                        chord.notes.map((note) => note + root)
                     )
                  )
               ),
               of(exerciseChallengeReady())
            );
         }

         return merge(
            of(clearSelectedNotes()),
            of(
               setReferenceNotes(
                  arrayValuesToObjectKeys(
                     createChord(
                        level,
                        state$.value.activeExercise.exerciseDocument,
                        state$.value.activeInterface.noteRange
                     )
                  )
               )
            ),
            of(exerciseChallengeReady())
         );
      })
   );

const completeSessionEpic = (action$) =>
   action$.pipe(
      ofType(COMPLETE_SESSION),
      switchMap(() =>
         merge(of(clearSelectedNotes()), of(setReferenceNotes({})))
      )
   );

const submitAnswerEpic = (action$, state$) =>
   action$.pipe(
      ofType(PRE_SUBMIT_ANSWER),
      switchMap(() => {
         const metadata = state$.value.activeExercise.metadata;
         if (metadata?.mode === keyboardModes.nameByEar) {
            const foundChord =
               state$.value.activeExercise.exerciseDocument.levels[
                  metadata.level - 1
               ].chords.find((chord) =>
                  Object.entries(state$.value.activeInterface.chordInfo).reduce(
                     (matches, [parameterKey, parameterData]) => {
                        if (!matches) {
                           return false;
                        }
                        return chord[parameterKey] === parameterData.selected;
                     },
                     true
                  )
               );

            if (foundChord != null) {
               return merge(
                  of(
                     setSelectedNotes(
                        arrayValuesToObjectKeys(
                           foundChord.notes.map((note) => note + metadata.root)
                        )
                     )
                  ),
                  of(submitAnswer())
               );
            }

            return merge(of(submitAnswer()));
         }

         if (metadata) {
            return merge(
               of(
                  setReferenceNotes(getReferenceNotesFromMetaData(state$.value))
               ),
               of(setHiddenControls({ playReference: false })),
               of(submitAnswer())
            );
         }

         return merge(of(submitAnswer()));
      })
   );

export const intervalExerciseEpics = combineExerciseEpics(
   prepareNextChallengeEpic,
   prepareNextSessionEpic,
   completeSessionEpic,
   submitAnswerEpic
);
